import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    Checkbox,
    Chip,
    Divider,
    IconButton,
    Typography,
    CircularProgress,
    useTheme,
    TextField, Drawer,
} from "@mui/material";
import { Close, SearchOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {useAppSelector} from "../../../../Config/Hooks";
import {hasPermission} from "../../../../Shared/Auth/AuthenticationUtil";
import {AUTHORITIES} from "../../../../Config/Constants";
import {useQueryClient} from "react-query";
import {AssociateUsersToProfileReactQuery, UsersAssociationReactQuery} from "../ProfilesReactQuery";

interface User {
    id: number;
    name: string;
    email: string;
    isAssociated: boolean;
}
interface UserAssociationPanelProps {
    open: boolean;
    onClose: () => void;
    profileId: number;
    profileName: string;
}

export default function UserAssociationPanel({
                                                 open,
                                                 onClose,
                                                 profileId,
                                                 profileName,
                                             }: UserAssociationPanelProps) {

    const theme = useTheme();
    const queryClient = useQueryClient();
    const account = useAppSelector(state => state.authentication.account);
    const isAuthorizedToSave = hasPermission(account?.authoritySet, [
        AUTHORITIES.SYSADMIN, AUTHORITIES.USER_AUTHORITY_GROUP_MANAGER
    ]);

    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
    const [filterType, setFilterType] = useState<"all" | "withAccess" | "withoutAccess">("all");
    const mutationSave = AssociateUsersToProfileReactQuery();

    const { data, isLoading } = UsersAssociationReactQuery(profileId, open);
    const [users, setUsers] = useState<User[]>([]);
    const [openSearch, setOpenSearch] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (!open) return;

        setFilterType("all");
        setUsers([]);
        setSelectedUsers([]);
        setSearchTerm("");
        setOpenSearch(false);
    }, [open]);

    useEffect(() => {
        if (!open || !data?.data) return;

        setUsers(data.data);
        setSelectedUsers(data.data.filter((u: User) => u.isAssociated).map((u: User) => u.id));
    }, [data?.data, open]);


    const filteredUsers = useMemo(() => {
        if (filterType === "all") return users;
        if (filterType === "withAccess") return users.filter((u) => u.isAssociated);
        return users.filter((u) => !u.isAssociated);
    }, [users, filterType]);

    const handleToggleUser = (userId: number) => {
        setSelectedUsers((prev) =>
            prev.includes(userId)
                ? prev.filter((id) => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSelectAll = () => {
        const filteredIds = filteredUsers.map((u) => u.id);
        const allFilteredSelected =
            filteredIds.length > 0 && filteredIds.every((id) => selectedUsers.includes(id));

        if (allFilteredSelected) {
            setSelectedUsers((prev) => prev.filter((id) => !filteredIds.includes(id)));
        } else {
            setSelectedUsers((prev) => Array.from(new Set([...prev, ...filteredIds])));
        }
    };

    const isAllSelected =
        filteredUsers.length > 0 &&
        filteredUsers.every((u) => selectedUsers.includes(u.id));

    useEffect(() => {
        const allUsers = data?.data || [];
        if (searchTerm.length > 0) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            setUsers(
                (allUsers)?.filter(
                    (u) =>
                        u.name.toLowerCase().includes(lowerSearchTerm) ||
                        u.email.toLowerCase().includes(lowerSearchTerm)
                )
            );
        } else {
            setUsers(allUsers);
        }
    }, [searchTerm, data?.data]);


    const saveProfileUsers = (userIds: number[]) => {
        mutationSave.mutate(
            { authorityGroupId: profileId, userList: userIds },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries(["users-profile-association", profileId]);
                    onClose();
                },
            }
        );
    };


    return (
        <Drawer
                open={open}
                anchor="right"
                PaperProps={{
                    sx: {
                          width: '418px',
                          mt: '60px',
                          backgroundColor: theme.palette.background.default
                    }
                }}
                onClose={onClose}
                aria-labelledby="drawer-sidebar-title"
        >
            <Box sx={{ p: 3, pb: 0 }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Typography
                        id="drawer-sidebar-title"
                        variant="h2"
                        sx={{
                            fontSize: 24,
                        }}
                    >
                        Associar usuários
                    </Typography>
                    <IconButton
                        aria-label="Fechar"
                        onClick={onClose}
                        sx={{
                            p: "5px",
                            borderRadius: "100px",
                            color:theme.palette.primary.main,
                            "&:hover": {
                                backgroundColor: "rgba(45, 93, 231, 0.1)",
                            },
                        }}
                    >
                        <Close sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>

                <Divider
                    sx={{
                        backgroundColor: "#FAFBFF",
                        borderColor: "rgba(0,0,0,0.12)",
                        mb: 2,
                    }}
                />

                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 14,
                            flex: 1,
                            letterSpacing: "0.17px",
                        }}
                    >
                        Perfil de acesso
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            flex: 1,
                            textAlign: "right",
                        }}
                    >
                        {profileName}
                    </Typography>
                </Box>

                <Divider sx={{backgroundColor: "#FAFBFF", borderColor: "rgba(0,0,0,0.12)", mb: 2 }} />

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Chip
                            label="Com acesso"
                            variant={filterType === "withAccess" ? "filled" : "outlined"}
                            onClick={() => setFilterType(filterType === "withAccess" ? "all" : "withAccess")}
                            sx={{
                                height: 28,
                                fontSize: 13,
                                borderRadius: "100px",
                                backgroundColor:
                                    filterType === "withAccess" ? theme.palette.primary.main : "transparent",
                                color: filterType === "withAccess" ? theme.palette.mode === "light" ? "#FFFFFF" : "rgba(0,0,0,0.87)" : theme.palette.mode === "light" ? "rgba(0,0,0,0.87)" : "#FFFFFF",
                                border:
                                    filterType === "withAccess"
                                        ? "none"
                                        : "1px solid #BDBDBD",
                                "&:hover": {
                                    backgroundColor:
                                        filterType === "withAccess"
                                            ? "#2454C4"
                                            : "rgba(0,0,0,0.04)",
                                },
                            }}
                        />
                        <Chip
                            label="Sem acesso"
                            variant={filterType === "withoutAccess" ? "filled" : "outlined"}
                            onClick={() =>
                                setFilterType(
                                    filterType === "withoutAccess" ? "all" : "withoutAccess"
                                )
                            }
                            sx={{
                                height: 28,
                                fontSize: 13,
                                borderRadius: "100px",
                                backgroundColor:
                                    filterType === "withoutAccess" ? theme.palette.primary.main : "transparent",
                                color:
                                    filterType === "withoutAccess"
                                        ? theme.palette.mode === "light" ? "#FFFFFF" : "rgba(0,0,0,0.87)" : theme.palette.mode === "light" ? "rgba(0,0,0,0.87)" : "#FFFFFF",
                                border:
                                    filterType === "withoutAccess"
                                        ? "none"
                                        : "1px solid #BDBDBD",
                                "&:hover": {
                                    backgroundColor:
                                        filterType === "withoutAccess"
                                            ? "#2454C4"
                                            : "rgba(0,0,0,0.04)",
                                },
                            }}
                        />
                    </Box>

                    <IconButton
                        aria-label="Buscar usuários"
                        aria-expanded={openSearch}
                        onClick={() => setOpenSearch((prev) => !prev)}
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "100%",
                            backgroundColor: theme.palette.primary.main,
                            flexShrink: 0,
                            "&:hover": {
                                backgroundColor: theme.palette.primary.main,
                            },
                        }}
                    >
                        <SearchOutlined
                            sx={{
                                fontSize: 24,
                                color: theme.palette.mode === "light" ? "#FFFFFF" : "#051F36",
                            }}
                        />
                    </IconButton>
                </Box>
                {openSearch &&
                    <Box sx={{ pt: 2}}>
                        <TextField
                            placeholder="Buscar"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            fullWidth
                            variant="outlined"
                            size="small"
                            InputProps={{
                                style: {
                                    borderRadius: 4,
                                },
                                endAdornment: (
                                    <SearchOutlined
                                        sx={{
                                            fontSize: 20,
                                            color: theme.palette.text.primary,
                                            mr: 1,
                                        }}
                                    />
                                ),
                            }}
                            sx={{ width: "100%" }}
                        />
                    </Box>

                }
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflow: "auto",
                    m:2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    backgroundColor: theme.palette.mode === "light" ? "#FAFBFF" : "rgba(81, 81, 81, 1)",
                    color: theme.palette.text.primary,
                    "&::-webkit-scrollbar": {
                        width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "#EBF0FF",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#A7A8AC",
                        "&:hover": {
                            backgroundColor: "#888",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        m: '0 16px',
                    }}
                >
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {filterType === "withoutAccess"
                                ? (data?.data?.length ?? 0) - selectedUsers.length
                                : selectedUsers.length}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 400,
                                color: theme.palette.text.primary,
                            }}
                        >
                            de {data?.data?.length ?? 0} usuários
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Typography
                            component="span"
                            role="button"
                            tabIndex={0}
                            onClick={handleSelectAll}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    handleSelectAll();
                                }
                            }}
                            sx={{
                                fontSize: 12,
                                fontWeight: 500,
                                color: theme.palette.text.primary,
                                cursor: "pointer",
                                "&:hover": {
                                    textDecoration: "underline",
                                },
                            }}
                        >
                            Selecionar todos
                        </Typography>
                        <Checkbox
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                            inputProps={{ "aria-label": "Selecionar todos os usuários" }}
                            sx={{
                                p: "9px",
                                "& .MuiSvgIcon-root": { fontSize: 20 },
                            }}
                        />
                    </Box>
                </Box>

                <Divider
                    sx={{
                        m: '0 16px',
                        backgroundColor: "#FAFBFF",
                    }}
                />
                {isLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={40} />
                    </Box>
                )}
                {!isLoading && filteredUsers.length === 0 && (
                    <Typography
                        sx={{
                            fontSize: 14,
                            color: theme.palette.text.primary,
                            textAlign: "center",
                            py: 4,
                        }}
                    >
                        Nenhum usuário encontrado
                    </Typography>
                )}

                {!isLoading &&
                    filteredUsers.map((user) => {
                        return(
                            <Box
                                key={user.id}
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 2,
                                    p: 1.5,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        backgroundColor:theme.palette.mode === "light" ? "#F5F9FF" : "rgb(92, 89, 89)",
                                        borderColor: "#E0E7FF",
                                    },
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 600,
                                            color: theme.palette.text.primary,
                                            lineHeight: "20px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {user.name}
                                    </Typography>
                                    <Typography
                                        title={user.email}
                                        sx={{
                                            fontSize: 14,
                                            fontWeight: 400,
                                            color: theme.palette.text.primary,
                                            lineHeight: "20px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {user.email}
                                    </Typography>
                                </Box>
                                <Checkbox
                                    checked={selectedUsers.includes(user.id)}
                                    onChange={() => handleToggleUser(user.id)}
                                    inputProps={{ "aria-label": `Associar ${user.name}` }}
                                    sx={{
                                        p: "9px",
                                        flexShrink: 0,
                                        "& .MuiSvgIcon-root": {
                                            fontSize: 20,
                                        },
                                    }}
                                />
                            </Box>
                        )})
                }
            </Box>
            <Divider sx={{ backgroundColor: "#FAFBFF", borderColor: "rgba(0,0,0,0.12)", mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 2, mb: 2, padding: '0 24px' }}>
                <LoadingButton
                    variant="contained"
                    color="primary"
                    loading={isLoading || mutationSave.isLoading}
                    onClick={() => saveProfileUsers(selectedUsers)}
                    disabled={!isAuthorizedToSave || isLoading || mutationSave.isLoading || selectedUsers.length === 0}
                >
                    ASSOCIAR USUÁRIOS
                </LoadingButton>
                <LoadingButton variant="outlined" color="secondary" onClick={onClose} loading={isLoading || mutationSave.isLoading}>
                    CANCELAR
                </LoadingButton>
            </Box>
        </Drawer>
    );
}
